import apiClient from './apiClient'
import { getAppConfig } from './appConfig'

const { useMockData } = getAppConfig()

export async function fetchTrades(page, pageSize, search = '', sortBy = []) {
  if (useMockData) {
    const response = await fetch('/mock/trades.json');
    return await response.json();
  }
  const params = { page, page_size: pageSize };
  if (search) params.search = search;
  if (sortBy && sortBy.length > 0) {
    params.sort_by = sortBy.map(s => `${s.key}:${s.order}`).join(',');
  }
  const response = await apiClient.get('/trades/', { params });
  return response.data;
}

export async function fetchLinkedBuyTrades(e2etransactionid) {
  if (useMockData) {
    return [];
  }
  const response = await apiClient.get('/trades/linked-buys/', { params: { e2etransactionid } });
  return response.data;
}

export async function fetchRealizedPnl() {
  if (useMockData) {
    return { realized_pnl: 0 };
  }
  const response = await apiClient.get('/realized_pnl/');
  return response.data;
}

// @deprecated — use fetchRealizedPnlByTransaction instead; kept for backward compatibility with other views
export async function fetchRealizedPnlForTicker(ticker) {
  if (useMockData) {
    return { ticker, realized_pnl: 0 };
  }
  const response = await apiClient.get(`/admin/realized-pnl/${ticker}`);
  return response.data;
}

export async function fetchRealizedPnlByTransaction(e2etransactionid) {
  if (useMockData) {
    return { e2etransactionid, realized_pnl: null };
  }
  const response = await apiClient.get(`/admin/realized-pnl-by-txn/${e2etransactionid}`);
  return response.data;
}

export async function fetchPortfolio() {
  if (useMockData) {
    const portfolio = await fetch('/mock/positions.json');
    return await portfolio.json();
  }
  const response = await apiClient.get('/portfolio/');
  return response.data;
}

// Live state of the portfolio drawdown guard. Piggybacks on the same
// /portfolio/ payload the dashboard already polls; returns just the dd_guard
// block (or null when absent / mocked).
export async function fetchDdGuardState() {
  if (useMockData) {
    return null;
  }
  const response = await apiClient.get('/portfolio/');
  return response.data?.dd_guard ?? null;
}

export async function validateTicker(ticker) {
  if (useMockData) {
    return { valid: true, name: `${ticker} Inc. (Mock)` };
  }
  const response = await apiClient.get('/validate-ticker/', { params: { ticker } });
  return response.data;
}

export async function fetchSuggestions() {
  if (useMockData) {
    return { suggestions: [] };
  }
  const response = await apiClient.get('/suggestions/');
  return response.data;
}

export async function fetchTickerPrice(ticker) {
  const response = await apiClient.get(`/admin/price/${ticker}`);
  return response.data;
}

/**
 * Fetches the live macro market status.
 *
 * @returns {Promise<{
 *   active_profile: string,
 *   market_outlook: 'BULLISH'|'BEARISH'|'SIDEWAYS'|'VOLATILE'|null,
 *   tradeable: boolean,
 *   setup_flavor: string|null,
 *   confidence: number|null,
 *   prediction_available: boolean,
 *   per_index_strategies: Record<'SPY'|'QQQ'|'DIA'|'IWM', ('AGGRESSIVE'|'CONSERVATIVE'|'DEFENSIVE'|null)>,
 *   per_index_outlooks: Record<'SPY'|'QQQ'|'DIA'|'IWM', ({outlook: string, confidence: number|null}|null)>,
 *   retrieved_at: string,
 * }>}
 */
export async function fetchMarketStatus() {
  const response = await apiClient.get('/market-status/');
  const data = response.data;
  console.info('[api] fetchMarketStatus success', data);
  return data;
}

/**
 * Fetches the current system clock — the wall clock the backend runs on.
 * Under the backtest harness this is the (faked) virtual-now; live it is real now.
 * `paused` is true while the backtest-driver holds the replay paused (always false live).
 *
 * @returns {Promise<{ utc: string, backtest: boolean, paused: boolean }>}
 */
export async function fetchSystemClock() {
  const response = await apiClient.get('/system/clock');
  return response.data;
}

/**
 * Fetches the current daily watchlist.
 *
 * @returns {Promise<{
 *   last_built_at: string|null,
 *   entries: Array<{
 *     ticker: string,
 *     qualifier_type: string,
 *     reason: string,
 *     timestamp_utc: string,
 *     index: string,
 *   }>,
 *   retrieved_at: string,
 * }>}
 */
export async function fetchWatchlist() {
  if (useMockData) {
    return { last_built_at: null, entries: [], retrieved_at: new Date().toISOString() };
  }
  console.info('[api] fetchWatchlist request');
  const response = await apiClient.get('/watchlist/');
  const data = response.data;
  console.info('[api] fetchWatchlist success', {
    status: response.status,
    count: Array.isArray(data?.entries) ? data.entries.length : 0,
    last_built_at: data?.last_built_at ?? null,
  });
  return data;
}

/**
 * Sessions with a recorded watchlist, newest first.
 *
 * @returns {Promise<{dates: Array<{session_date: string, ticker_count: number,
 *   entry_count: number, up_count: number, down_count: number,
 *   last_recorded_at: string|null}>}>}
 */
export async function fetchWatchlistHistoryDates(limit = 90) {
  if (useMockData) return { dates: [] };
  const response = await apiClient.get('/watchlist/history/dates', { params: { limit } });
  return response.data;
}

/**
 * Recorded watchlist entries for a session and/or a ticker. With neither,
 * the most recent recorded session.
 *
 * @param {{date?: string, ticker?: string}} filters
 * @returns {Promise<{session_date: string|null, ticker: string|null, entries: Array<object>}>}
 */
export async function fetchWatchlistHistory({ date, ticker } = {}) {
  if (useMockData) return { session_date: null, ticker: null, entries: [] };
  const params = {};
  if (date) params.date = date;
  if (ticker) params.ticker = ticker;
  const response = await apiClient.get('/watchlist/history', { params });
  return response.data;
}

/**
 * The same rows as CSV (analysis flattened into analysis_* columns), saved
 * through a temporary link so the admin auth header travels with the request.
 */
export async function downloadWatchlistHistoryCsv({ date, ticker } = {}) {
  const params = { format: 'csv' };
  if (date) params.date = date;
  if (ticker) params.ticker = ticker;
  const response = await apiClient.get('/watchlist/history', { params, responseType: 'blob' });
  const url = URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = `watchlist_${date || 'all'}${ticker ? `_${ticker}` : ''}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function uploadWatchlistSuggestions(file, defaultDirection) {
  const formData = new FormData();
  formData.append('file', file);
  if (defaultDirection) {
    formData.append('default_direction', defaultDirection);
  }
  const response = await apiClient.post('/watchlist/suggestions/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
}

export async function suggestWatchlistTicker(ticker, direction) {
  const response = await apiClient.post('/watchlist/suggestion', { ticker, direction });
  return response.data;
}

export async function searchTickers(query) {
  if (useMockData) {
    return [];
  }
  console.info('[api] searchTickers query:', query);
  try {
    const response = await apiClient.get('/search-tickers/', { params: { query } });
    const results = Array.isArray(response.data) ? response.data : [];
    console.info('[api] searchTickers results count:', results.length);
    return results;
  } catch (error) {
    console.error('[api] searchTickers error for query:', query, error.message);
    return [];
  }
}

/**
 * Places a buy or sell order for a given ticker.
 *
 * @param {string} ticker - The stock ticker symbol to trade.
 * @param {string} side - The order side, either 'buy' or 'sell'.
 * @param {number} qty - The number of shares to order.
 * @param {Object} [opts] - Optional extras forwarded to the backend.
 * @param {string} [opts.position_side] - 'LONG' or 'SHORT'.
 * @param {string} [opts.intent] - Exit intent (e.g. 'SHORT_COVER').
 * @returns {Promise<Object>} The order response data from the API.
 */
export async function placeOrder(ticker, side, qty, opts = {}) {
  const payload = { ticker, side, qty };
  if (opts && opts.position_side) payload.position_side = opts.position_side;
  if (opts && opts.intent) payload.intent = opts.intent;
  const response = await apiClient.post('/orders/', payload);
  return response.data;
}

/**
 * Durable broker orders (broker_orders), newest first. Every filter is
 * optional; state and end_reason take comma-separated lists. The legs of each
 * bracket entry on the page come back under `legs` even when a filter would
 * exclude them, so an entry can be drawn with its two legs under it.
 *
 * @param {{session_date?: string, ticker?: string, bracket_role?: string,
 *   state?: string, end_reason?: string, limit?: number, offset?: number}} filters
 * @returns {Promise<{orders: Array<object>, legs: Array<object>, total: number,
 *   limit: number, offset: number}>}
 */
export async function fetchOrderHistory(filters = {}) {
  if (useMockData) return { orders: [], legs: [], total: 0, limit: 200, offset: 0 };
  const params = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') params[key] = value;
  }
  const response = await apiClient.get('/orders/history', { params });
  return response.data;
}
